import React from 'react';
import styled from 'styled-components';

export interface SegmentedOption<T extends string = string> {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
}

interface SegmentedControlProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  disabled?: boolean;
  className?: string;
}

const Container = styled.div`
  display: inline-flex;
  background: ${(p) => p.theme.lightGray};
  padding: 4px;
  border-radius: ${(p) => p.theme.radius};
`;

const Segment = styled.button<{ $active?: boolean }>`
  border: none;
  background: ${(p) => (p.$active ? p.theme.surface : 'transparent')};
  padding: 6px 16px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  color: ${(p) => (p.$active ? p.theme.textPrimary : p.theme.textMuted)};
  box-shadow: ${(p) => (p.$active ? p.theme.shadowSm : 'none')};
  transition: all 0.2s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

/** Segmented control ("Lista | Kanban" style) for mutually exclusive options. */
export function SegmentedControl<T extends string = string>({
  value,
  onChange,
  options,
  disabled,
  className,
}: SegmentedControlProps<T>) {
  return (
    <Container className={className} role="tablist">
      {options.map((option) => (
        <Segment
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          $active={option.value === value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          {option.icon}
          {option.label}
        </Segment>
      ))}
    </Container>
  );
}

export default SegmentedControl;
